import type { Editor } from '@tiptap/react';
import type { Observable } from './Observable.ts';
import type { UndoRedoCommandInterface } from './undoRedoCommandVersion.ts';

export type UndoRedoStateChangeListener = () => void;

/**
 * Focus context tracks which editor/block is currently active
 * to route undo/redo to the correct handler
 */
export interface EditorFocusContext {
  focusedBlockId: string | null;
  focusedBlockType: 'text' | 'table' | null;
  hasTiptapFocus: boolean;
}

export interface SectionCommand extends UndoRedoCommandInterface {
  readonly scope: 'section';
  readonly sectionId: string;
}

/**
 * Focus context tracks which editor/block is currently active
 * to route undo/redo to the correct handler
 */
export interface EditorFocusContext {
  focusedBlockId: string | null;
  focusedBlockType: 'text' | 'table' | null;
  hasTiptapFocus: boolean;
}

/**
 * Optional interface for block commands to support filtering
 */
export interface BlockCommand extends UndoRedoCommandInterface {
  readonly scope: 'block';
  getCurrentBlockId(): string;
  readonly sectionId: string;
}

/**
 * Optional interface for section commands to support filtering
 */
export interface SectionCommand extends UndoRedoCommandInterface {
  readonly scope: 'section';
  readonly sectionId: string;
}

/**
 * Command that wraps multiple commands for atomic undo/redo
 */
class BatchCommand implements UndoRedoCommandInterface {
  readonly scope = 'global' as const;
  readonly timestamp: string;
  private commands: UndoRedoCommandInterface[];

  constructor(commands: UndoRedoCommandInterface[]) {
    const now = new Date();
    this.commands = [...commands];
    this.timestamp = now.toISOString();
  }

  async undo(): Promise<boolean> {
    for (let i = this.commands.length - 1; i >= 0; i--) {
      const success = await this.commands[i].undo();
      if (!success) {
        console.error(`BatchCommand: Command ${i} (${this.commands[i].constructor.name}) failed to undo`);
      }
    }
    return true;
  }

  async redo(): Promise<boolean> {
    for (let i = 0; i < this.commands.length; i++) {
      const success = await this.commands[i].redo();
      if (!success) {
        console.error(`BatchCommand: Command ${i} (${this.commands[i].constructor.name}) failed to redo`);
      }
    }
    return true;
  }

  /**
   * Get description of batched commands (for debugging)
   */
  getDescription(): string {
    const commandNames = this.commands.map((c) => c.constructor.name).join(', ');
    return `Batch of ${this.commands.length} commands: ${commandNames}`;
  }

  toJSON(): Record<string, any> {
    return {
      commands: this.commands
        .filter((cmd) => cmd.toJSON)
        .map((cmd) => ({
          type: cmd.constructor.name,
          data: cmd.toJSON!(),
        })),
    };
  }
}

/**
 * Enhanced UndoRedoManager that coordinates global block operations
 * with Tiptap's internal undo/redo system
 */
export class UndoRedoManagerCommandVersion implements Observable {
  private static undoRedoManagerInstance: UndoRedoManagerCommandVersion;

  private undoStack: UndoRedoCommandInterface[];
  private redoStack: UndoRedoCommandInterface[];
  private readonly stackLimit: number;
  private isRunningACommand: boolean;

  private listeners: Set<UndoRedoStateChangeListener>;
  private tiptapEditors: Map<string, Editor>;
  private focusContext: EditorFocusContext;

  private currentBatch: UndoRedoCommandInterface[] | null;
  private isBatching: boolean;

  private constructor() {
    this.undoStack = [];
    this.redoStack = [];
    this.stackLimit = 20;
    this.isRunningACommand = false;
    this.listeners = new Set();
    this.tiptapEditors = new Map();
    this.focusContext = {
      focusedBlockId: null,
      focusedBlockType: null,
      hasTiptapFocus: false,
    };
    this.currentBatch = null;
    this.isBatching = false;
  }

  static getUndoRedoManager(): UndoRedoManagerCommandVersion {
    if (!UndoRedoManagerCommandVersion.undoRedoManagerInstance) {
      UndoRedoManagerCommandVersion.undoRedoManagerInstance = new UndoRedoManagerCommandVersion();
    }
    return UndoRedoManagerCommandVersion.undoRedoManagerInstance;
  }

  subscribe(listener: UndoRedoStateChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyStateChange(): void {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        console.error('Error in undo/redo listener:', error);
      }
    });
  }

  private resetRedoStack(): void {
    this.redoStack = [];
    this.notifyStateChange();
  }

  registerTiptapEditor(blockId: string, editor: Editor): void {
    this.tiptapEditors.set(blockId, editor);
  }

  unregisterTiptapEditor(blockId: string): void {
    this.tiptapEditors.delete(blockId);
  }

  getTiptapEditor(blockId: string): Editor | undefined {
    return this.tiptapEditors.get(blockId);
  }

  hasCommandsForBlock(blockId: string): boolean {
    const checkCommand = (cmd: UndoRedoCommandInterface): boolean => {
      return (
        'getCurrentBlockId' in cmd && typeof (cmd as any).getCurrentBlockId === 'function' && (cmd as any).getCurrentBlockId() === blockId
      );
    };

    const hasInUndo = this.undoStack.some(checkCommand);
    const hasInRedo = this.redoStack.some(checkCommand);

    return hasInUndo || hasInRedo;
  }

  updateFocusContext(context: Partial<EditorFocusContext>): void {
    this.focusContext = { ...this.focusContext, ...context };
    this.notifyStateChange();
  }

  getFocusContext(): EditorFocusContext {
    return { ...this.focusContext };
  }

  startBatch(): void {
    if (this.isBatching) {
      console.warn('Already batching commands. Call endBatch() first.');
      return;
    }
    this.isBatching = true;
    this.currentBatch = [];
  }

  endBatch(): void {
    if (!this.isBatching || !this.currentBatch) {
      console.warn('Not currently batching commands.');
      return;
    }

    if (this.currentBatch.length === 0) {
      this.isBatching = false;
      this.currentBatch = null;
      return;
    }

    const batchCommand = new BatchCommand(this.currentBatch);
    this.isBatching = false;
    this.currentBatch = null;

    this.pushUndoRedoCommands(batchCommand);
  }

  cancelBatch(): void {
    this.isBatching = false;
    this.currentBatch = null;
    this.notifyStateChange();
  }

  isBatchingCommands(): boolean {
    return this.isBatching;
  }

  pushUndoRedoCommands(command: UndoRedoCommandInterface): void {
    if (this.isRunningACommand) {
      console.warn('Cannot push command while another command is running');
      return;
    }

    if (!command || typeof command.undo !== 'function' || typeof command.redo !== 'function') {
      console.error('Invalid command - must implement undo() and redo()', command);
      return;
    }

    if (this.undoStack.length > 0 && this.undoStack[this.undoStack.length - 1] === command) {
      console.warn('Preventing duplicate push of same command instance');
      return;
    }

    if (this.isBatching && this.currentBatch) {
      this.currentBatch.push(command);
      return;
    }

    if (this.undoStack.length >= this.stackLimit) {
      this.undoStack.shift();
    }

    this.undoStack.push(command);
    this.resetRedoStack();
    this.notifyStateChange();
  }

  isUndoStackEmpty(): boolean {
    return this.undoStack.length === 0;
  }

  isRedoStackEmpty(): boolean {
    return this.redoStack.length === 0;
  }

  canUndo(): boolean {
    return !this.isUndoStackEmpty();
  }

  canRedo(): boolean {
    return !this.isRedoStackEmpty();
  }

  async runUndoCommand(): Promise<void> {
    if (this.isRunningACommand) {
      console.warn('Cannot undo while another command is running');
      return;
    }

    const maxRetries = 3;
    let retries = 0;

    while (retries < maxRetries && this.undoStack.length > 0) {
      const commandToBeExecuted = this.undoStack.pop()!;
      this.isRunningACommand = true;

      try {
        const wasCommandSuccessful = await commandToBeExecuted.undo();

        if (wasCommandSuccessful) {
          this.redoStack.push(commandToBeExecuted);

          if (this.redoStack.length > this.stackLimit) {
            this.redoStack.shift();
          }
          break;
        }

        console.warn(`Undo failed for command: ${commandToBeExecuted.constructor.name} (attempt ${retries + 1}/${maxRetries})`);
        retries++;
      } catch (error) {
        console.error('Undo threw exception:', error, 'Command:', commandToBeExecuted.constructor.name);
        retries++;
      } finally {
        this.isRunningACommand = false;
      }
    }

    if (retries >= maxRetries) {
      console.error(`Undo gave up after ${maxRetries} consecutive failures`);
    }

    this.notifyStateChange();
  }

  async runRedoCommand(): Promise<void> {
    if (this.isRunningACommand) {
      console.warn('Cannot redo while another command is running');
      return;
    }

    const maxRetries = 3;
    let retries = 0;

    while (retries < maxRetries && this.redoStack.length > 0) {
      const commandToBeExecuted = this.redoStack.pop()!;
      this.isRunningACommand = true;

      try {
        const wasCommandSuccessful = await commandToBeExecuted.redo();

        if (wasCommandSuccessful) {
          this.undoStack.push(commandToBeExecuted);

          if (this.undoStack.length > this.stackLimit) {
            this.undoStack.shift();
          }
          break;
        }

        console.warn(`Redo failed for command: ${commandToBeExecuted.constructor.name} (attempt ${retries + 1}/${maxRetries})`);
        retries++;
      } catch (error) {
        console.error('Redo threw exception:', error, 'Command:', commandToBeExecuted.constructor.name);
        retries++;
      } finally {
        this.isRunningACommand = false;
      }
    }

    if (retries >= maxRetries) {
      console.error(`Redo gave up after ${maxRetries} consecutive failures`);
    }

    this.notifyStateChange();
  }

  clearBlockCommands(blockId: string): void {
    const isBlockCommand = (cmd: UndoRedoCommandInterface): boolean => {
      return cmd.scope === 'block' && 'getCurrentBlockId' in cmd && (cmd as BlockCommand).getCurrentBlockId() === blockId;
    };

    this.undoStack = this.undoStack.filter((cmd) => !isBlockCommand(cmd));
    this.redoStack = this.redoStack.filter((cmd) => !isBlockCommand(cmd));

    this.notifyStateChange();
  }

  clearSectionCommands(sectionId: string): void {
    const isSectionCommand = (cmd: UndoRedoCommandInterface): boolean => {
      return (cmd.scope === 'section' || cmd.scope === 'block') && 'sectionId' in cmd && (cmd as SectionCommand).sectionId === sectionId;
    };

    this.undoStack = this.undoStack.filter((cmd) => !isSectionCommand(cmd));
    this.redoStack = this.redoStack.filter((cmd) => !isSectionCommand(cmd));

    this.notifyStateChange();
  }

  clearHistory(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.currentBatch = null;
    this.isBatching = false;
    this.notifyStateChange();
  }

  /**
   * Undo multiple commands at once (up to a specific timestamp)
   * Useful for "undo all changes in last 5 minutes"
   */
  async undoToTimestamp(timestamp: string): Promise<number> {
    let undoneCount = 0;

    while (this.undoStack.length > 0) {
      const lastCommand = this.undoStack[this.undoStack.length - 1];

      if (lastCommand.timestamp < timestamp) {
        break;
      }

      await this.runUndoCommand();
      undoneCount++;
    }

    return undoneCount;
  }

  /**
   * Undo a specific number of commands
   */
  async undoMultiple(count: number): Promise<number> {
    let undoneCount = 0;

    for (let i = 0; i < count && this.undoStack.length > 0; i++) {
      await this.runUndoCommand();
      undoneCount++;
    }

    return undoneCount;
  }

  /**
   * Redo a specific number of commands
   */
  async redoMultiple(count: number): Promise<number> {
    let redoneCount = 0;

    for (let i = 0; i < count && this.redoStack.length > 0; i++) {
      await this.runRedoCommand();
      redoneCount++;
    }

    return redoneCount;
  }

  /**
   * Serialize undo stack to JSON (for persistence)
   * Commands must implement toJSON() to support this
   */
  serializeHistory(): string | null {
    try {
      const serializable = this.undoStack
        .filter((cmd) => cmd.toJSON)
        .map((cmd) => ({
          type: cmd.constructor.name,
          scope: cmd.scope,
          timestamp: cmd.timestamp,
          data: cmd.toJSON!(),
        }));

      return JSON.stringify({
        version: 1,
        commands: serializable,
      });
    } catch (error) {
      console.error('Failed to serialize history:', error);
      return null;
    }
  }

  /**
   * Restore undo stack from JSON
   * Requires command factory to reconstruct commands
   */
  deserializeHistory(json: string, commandFactory: (type: string, data: any) => UndoRedoCommandInterface | null): boolean {
    try {
      const parsed = JSON.parse(json);

      if (parsed.version !== 1) {
        console.error('Unsupported history version:', parsed.version);
        return false;
      }

      this.undoStack.length = 0;
      this.redoStack.length = 0;

      for (const item of parsed.commands) {
        const command = commandFactory(item.type, item.data);
        if (command) {
          this.undoStack.push(command);
        }
      }

      this.notifyStateChange();
      return true;
    } catch (error) {
      console.error('Failed to deserialize history:', error);
      return false;
    }
  }

  /**
   * Get current state for debugging
   */
  public getHistoryState(): {
    undoCount: number;
    redoCount: number;
    stackLimit: number;
    focus: EditorFocusContext;
    tiptapEditors: string[];
    isBatching: boolean;
    batchSize: number;
  } {
    return {
      undoCount: this.undoStack.length,
      redoCount: this.redoStack.length,
      stackLimit: this.stackLimit,
      focus: { ...this.focusContext },
      tiptapEditors: Array.from(this.tiptapEditors.keys()),
      isBatching: this.isBatching,
      batchSize: this.currentBatch?.length ?? 0,
    };
  }

  /**
   * Get preview of undo stack (for debugging/UI)
   */
  getUndoStackPreview(limit = 10): {
    scope: 'block' | 'section' | 'table' | 'document' | 'global';
    name: string;
    timestamp: string;
  }[] {
    return this.undoStack.slice(-limit).map((cmd) => ({
      name: cmd.constructor.name,
      scope: cmd.scope,
      timestamp: cmd.timestamp,
    }));
  }

  /**
   * Get preview of redo stack (for debugging/UI)
   */
  getRedoStackPreview(limit = 10): Array<{
    name: string;
    scope: string;
    timestamp: string;
  }> {
    return this.redoStack.slice(-limit).map((cmd) => ({
      name: cmd.constructor.name,
      scope: cmd.scope,
      timestamp: cmd.timestamp,
    }));
  }

  /**
   * Get stack sizes
   */
  getStackSizes(): { undo: number; redo: number } {
    return {
      undo: this.undoStack.length,
      redo: this.redoStack.length,
    };
  }

  /**
   * Check if a command is currently running
   */
  isCommandRunning(): boolean {
    return this.isRunningACommand;
  }
}

export { BatchCommand };
